import { request } from '.';

// ============================================================================
// Tipos
// ============================================================================
export type EezyguardStatus =
  | 'received'
  | 'under_review'
  | 'in_progress'
  | 'completed'
  | 'archived'
  | 'unsubstantiated';

export type EezyguardSeverity = 'baixa' | 'media' | 'alta' | 'critica';
export type EezyguardPriority = 'baixa' | 'media' | 'alta' | 'critica';
export type EezyguardNoteVisibility =
  | 'eezycare_internal'
  | 'company_internal'
  | 'shared';

export type OccurrenceTypeOption = { value: string; label: string };

export type PublicCompanyView = {
  available: boolean;
  slug?: string;
  company_name?: string | null;
  occurrence_types?: OccurrenceTypeOption[];
  severities?: EezyguardSeverity[];
};

export type OccurrenceListItem = {
  id: number;
  protocol: string;
  company_id: number;
  company_name: string | null;
  occurrence_type: string;
  occurrence_type_label: string;
  severity: EezyguardSeverity;
  priority: EezyguardPriority | null;
  status: EezyguardStatus;
  sector: string | null;
  is_anonymous: boolean;
  forwarded_to_eezycare: boolean;
  assigned_to_user_id: number | null;
  approximate_date: string | null;
  created_at: string;
};

export type OccurrenceListResult = {
  items: OccurrenceListItem[];
  total: number;
  page: number;
  page_size: number;
};

export type Occurrence = OccurrenceListItem & {
  occurrence_type_other: string | null;
  location: string | null;
  location_details: string | null;
  description: string | null;
  people_involved: string | null;
  witnesses: string | null;
  additional_information: string | null;
  forwarded_at: string | null;
  updated_at: string | null;
  closed_at: string | null;
  archived_at: string | null;
  // presentes apenas quando autorizado / visão eezycare
  identity_share_authorized?: boolean;
  forwarded_identity_included?: boolean;
  reporter_name?: string | null;
  reporter_email?: string | null;
  reporter_phone?: string | null;
  reporter_role?: string | null;
  reporter_relationship?: string | null;
};

export type Evidence = {
  id: number;
  original_filename: string | null;
  content_type: string | null;
  size_bytes: number | null;
  created_at: string;
};

export type Note = {
  id: number;
  author_scope: string;
  visibility: EezyguardNoteVisibility;
  content: string;
  created_at: string;
};

export type ActionPlan = {
  id: number;
  fk_occurrence_id: number;
  title: string;
  description: string | null;
  fk_responsible_user_id: number | null;
  due_date: string | null;
  status: string;
  created_at: string;
  completed_at: string | null;
};

export type HistoryEntry = {
  id: number;
  actor_scope: string;
  event_type: string;
  previous_value: string | null;
  new_value: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
};

export type OccurrenceDetail = {
  occurrence: Occurrence;
  evidences: Evidence[];
  notes: Note[];
  action_plans: ActionPlan[];
  history: HistoryEntry[];
};

export type DashboardMetrics = {
  totals: {
    total: number;
    open: number;
    completed: number;
    critical: number;
    awaiting_review: number;
    not_forwarded: number;
    unsubstantiated: number;
  };
  avg_hours: { to_forward: number | null; to_completion: number | null };
  by_type: Record<string, number>;
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
  by_severity: Record<string, number>;
  by_sector: Record<string, number>;
  timeline: { date: string; count: number }[];
};

export type CompanySettings = {
  id: number;
  fk_empresa_id: number;
  enabled: boolean;
  public_slug: string | null;
  enabled_at: string | null;
  disabled_at: string | null;
  notification_emails: string[];
};

export type CompanyUser = { id: number; name: string; email: string };

export type OccurrenceFilters = {
  company_id?: string;
  status?: string;
  occurrence_type?: string;
  severity?: string;
  priority?: string;
  sector?: string;
  assigned_to_user_id?: string;
  protocol?: string;
  date_from?: string;
  date_to?: string;
  forwarded?: boolean;
  page?: number;
  page_size?: number;
  sort?: string;
  order?: 'asc' | 'desc';
};

// ============================================================================
// Rótulos
// ============================================================================
export const STATUS_LABELS: Record<EezyguardStatus, string> = {
  received: 'Recebida',
  under_review: 'Em análise',
  in_progress: 'Em tratativa',
  completed: 'Concluída',
  archived: 'Arquivada',
  unsubstantiated: 'Improcedente',
};

export const SEVERITY_LABELS: Record<EezyguardSeverity, string> = {
  baixa: 'Baixa',
  media: 'Média',
  alta: 'Alta',
  critica: 'Crítica',
};

export const PRIORITY_LABELS: Record<EezyguardPriority, string> = {
  baixa: 'Baixa',
  media: 'Média',
  alta: 'Alta',
  critica: 'Crítica',
};

export const EVENT_LABELS: Record<string, string> = {
  occurrence_created: 'Ocorrência criada',
  viewed_by_eezycare: 'Visualizada pela Eezycare',
  viewed_by_company: 'Visualizada pela empresa',
  status_changed: 'Status alterado',
  priority_changed: 'Prioridade alterada',
  assignee_set: 'Responsável atribuído',
  assignee_removed: 'Responsável removido',
  note_added: 'Observação adicionada',
  evidence_linked: 'Evidência vinculada',
  forwarded_to_eezycare: 'Encaminhada à EezyCare',
  action_plan_created: 'Plano de ação criado',
  action_plan_updated: 'Plano de ação atualizado',
  occurrence_completed: 'Ocorrência concluída',
  occurrence_archived: 'Ocorrência arquivada',
  occurrence_unsubstantiated: 'Marcada como improcedente',
};

function toParams(filters: OccurrenceFilters = {}): Record<string, string> {
  const params: Record<string, string> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = String(value);
    }
  });
  return params;
}

// ============================================================================
// Capacidade / gate admin
// ============================================================================
export async function getEezyguardCapabilities(): Promise<{
  is_eezyguard_admin: boolean;
}> {
  const { data } = await request.get('/api/eezyguard/capabilities');
  return data;
}

// ============================================================================
// Público (sem auth)
// ============================================================================
export async function getPublicCompany(slug: string): Promise<PublicCompanyView> {
  const { data } = await request.get<PublicCompanyView>(
    `/api/public/eezyguard/company/${slug}`
  );
  return data;
}

export type CreateOccurrencePayload = {
  is_anonymous: boolean;
  occurrence_type: string;
  occurrence_type_other?: string;
  severity: EezyguardSeverity;
  sector?: string;
  location?: string;
  location_details?: string;
  approximate_date?: string;
  description: string;
  people_involved?: string;
  witnesses?: string;
  additional_information?: string;
  privacy_accepted: boolean;
  truthfulness_confirmed: boolean;
  identity_share_authorized?: boolean;
  reporter_name?: string;
  reporter_email?: string;
  reporter_phone?: string;
  reporter_role?: string;
  reporter_relationship?: string;
};

export async function createPublicOccurrence(
  slug: string,
  payload: CreateOccurrencePayload
): Promise<{ protocol: string; occurrence_id: number }> {
  const { data } = await request.post(
    `/api/public/eezyguard/company/${slug}/occurrences`,
    payload
  );
  return data;
}

export async function uploadPublicEvidences(
  slug: string,
  occurrenceId: number,
  files: File[],
  onProgress?: (percent: number) => void
): Promise<{ evidences: { id: number }[] }> {
  const form = new FormData();
  files.forEach((f) => form.append('files', f));
  const { data } = await request.post(
    `/api/public/eezyguard/company/${slug}/occurrences/${occurrenceId}/uploads`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    }
  );
  return data;
}

// ============================================================================
// Fábrica de cliente por escopo (admin | empresa)
// ============================================================================
export type EezyguardScope = 'admin' | 'company';

export type EezyguardClient = {
  scope: EezyguardScope;
  getDashboard: (filters?: OccurrenceFilters) => Promise<DashboardMetrics>;
  listOccurrences: (filters?: OccurrenceFilters) => Promise<OccurrenceListResult>;
  getOccurrence: (id: number) => Promise<OccurrenceDetail>;
  patchOccurrence: (id: number, body: Record<string, unknown>) => Promise<Occurrence>;
  addNote: (
    id: number,
    body: { content: string; visibility: EezyguardNoteVisibility }
  ) => Promise<Note>;
  addActionPlan: (id: number, body: Partial<ActionPlan>) => Promise<ActionPlan>;
  updateActionPlan: (
    id: number,
    planId: number,
    body: Partial<ActionPlan>
  ) => Promise<ActionPlan>;
  getEvidenceUrl: (id: number, evidenceId: number) => Promise<string>;
  pdfUrl: (id: number) => string;
  exportUrl: (filters?: OccurrenceFilters) => string;
};

function base(scope: EezyguardScope): string {
  return scope === 'admin' ? '/api/admin/eezyguard' : '/api/company/eezyguard';
}

export function createEezyguardClient(scope: EezyguardScope): EezyguardClient {
  const b = base(scope);
  return {
    scope,
    async getDashboard(filters = {}) {
      const { data } = await request.get<DashboardMetrics>(`${b}/dashboard`, {
        params: toParams(filters),
      });
      return data;
    },
    async listOccurrences(filters = {}) {
      const { data } = await request.get<OccurrenceListResult>(`${b}/occurrences`, {
        params: toParams(filters),
      });
      return data;
    },
    async getOccurrence(id) {
      const { data } = await request.get<OccurrenceDetail>(`${b}/occurrences/${id}`);
      return data;
    },
    async patchOccurrence(id, body) {
      const { data } = await request.patch<Occurrence>(`${b}/occurrences/${id}`, body);
      return data;
    },
    async addNote(id, body) {
      const { data } = await request.post<Note>(`${b}/occurrences/${id}/notes`, body);
      return data;
    },
    async addActionPlan(id, body) {
      const { data } = await request.post<ActionPlan>(
        `${b}/occurrences/${id}/action-plans`,
        body
      );
      return data;
    },
    async updateActionPlan(id, planId, body) {
      const { data } = await request.patch<ActionPlan>(
        `${b}/occurrences/${id}/action-plans/${planId}`,
        body
      );
      return data;
    },
    async getEvidenceUrl(id, evidenceId) {
      const { data } = await request.get<{ url: string }>(
        `${b}/occurrences/${id}/evidences/${evidenceId}`
      );
      return data.url;
    },
    pdfUrl(id) {
      return `${b}/occurrences/${id}/pdf`;
    },
    exportUrl(filters = {}) {
      const qs = new URLSearchParams(toParams(filters)).toString();
      return `${b}/export${qs ? `?${qs}` : ''}`;
    },
  };
}

// ============================================================================
// Empresa: encaminhamento para a EezyCare
// ============================================================================
export async function forwardToEezycare(id: number): Promise<Occurrence> {
  const { data } = await request.post<Occurrence>(
    `/api/company/eezyguard/occurrences/${id}/forward`,
    {}
  );
  return data;
}

// ============================================================================
// Admin: configuração por empresa
// ============================================================================
export async function getCompanySettings(companyId: number): Promise<CompanySettings> {
  const { data } = await request.get<CompanySettings>(
    `/api/admin/eezyguard/companies/${companyId}/settings`
  );
  return data;
}

export async function setCompanyEnabled(
  companyId: number,
  enabled: boolean
): Promise<CompanySettings> {
  const { data } = await request.patch<CompanySettings>(
    `/api/admin/eezyguard/companies/${companyId}/settings`,
    { enabled }
  );
  return data;
}

export async function regenerateCompanySlug(
  companyId: number
): Promise<CompanySettings> {
  const { data } = await request.post<CompanySettings>(
    `/api/admin/eezyguard/companies/${companyId}/regenerate-slug`,
    {}
  );
  return data;
}

export async function setCompanyNotificationEmails(
  companyId: number,
  emails: string[]
): Promise<CompanySettings> {
  const { data } = await request.patch<CompanySettings>(
    `/api/admin/eezyguard/companies/${companyId}/settings`,
    { notification_emails: emails }
  );
  return data;
}

export async function getCompanyUsers(companyId: number): Promise<CompanyUser[]> {
  const { data } = await request.get<CompanyUser[]>(
    `/api/admin/eezyguard/companies/${companyId}/users`
  );
  return data;
}

// ============================================================================
// Empresa: link público
// ============================================================================
export async function getCompanyPublicLink(): Promise<{
  enabled: boolean;
  public_slug: string | null;
  enabled_at: string | null;
  has_access: boolean;
}> {
  const { data } = await request.get('/api/company/eezyguard/public-link');
  return data;
}

// Empresa (ADM-EMPRESA): e-mails que recebem apenas as notificações (sem painel).
export async function getMyNotificationEmails(): Promise<string[]> {
  const { data } = await request.get<{ emails: string[] }>(
    '/api/company/eezyguard/notification-emails'
  );
  return data.emails ?? [];
}

export async function setMyNotificationEmails(emails: string[]): Promise<string[]> {
  const { data } = await request.put<{ emails: string[] }>(
    '/api/company/eezyguard/notification-emails',
    { emails }
  );
  return data.emails ?? [];
}

export function buildPublicFormUrl(slug: string | null | undefined): string {
  if (!slug) return '';
  const base =
    process.env.REACT_APP_EEZYGUARD_PUBLIC_BASE_URL || window.location.origin;
  return `${base.replace(/\/$/, '')}/eezyguard/denuncia/${slug}`;
}
